// ================================================
// IPMS Analytics Dashboard Component
// client/src/pages/IpmsAnalyticsDashboard.js
// ================================================

import React, { useState } from 'react';
import { useFetch } from '../hooks/useFetch';
import '../styles/AnalyticsDashboard.css';

const IpmsAnalyticsDashboard = () => {
  const { data: dashboard, loading, error } = useFetch('/api/ipms/analytics/dashboard');
  const [activeTab, setActiveTab] = useState('IMPS');

  if (loading) return <div className="analytics-loading">Loading IMPS Analytics...</div>;
  if (error) return <div className="analytics-error">Error: {error}</div>;

  if (!dashboard) return null;

  const {
    IMPS,
    vehiclesByDepartment,
    fuelling,
    maintenance,
    documentation,
    costAnalysis,
    geographicDistribution
  } = dashboard;

  return (
    <div className="analytics-dashboard ipms-dashboard">
      <div className="dashboard-header">
        <h1>IMPS Management Analytics</h1>
        <p>Vehicle & Operations insights from IPMS</p>
      </div>

      {/* Key Metrics Cards */}
      <div className="metrics-grid">
        <MetricCard
          title="Total Vehicles"
          value={IMPS?.total_vehicles}
          icon="🚗"
          color="blue"
        />
        <MetricCard
          title="Active Vehicles"
          value={IMPS?.active_vehicles}
          icon="✅"
          color="green"
        />
        <MetricCard
          title="Departments"
          value={IMPS?.departments_with_vehicles}
          icon="🏢"
          color="purple"
        />
        <MetricCard
          title="Brands"
          value={IMPS?.brands_in_IMPS}
          icon="🏷️"
          color="orange"
        />
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button
          className={`tab-button ${activeTab === 'IMPS' ? 'active' : ''}`}
          onClick={() => setActiveTab('IMPS')}
        >
          IMPS Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'geographic' ? 'active' : ''}`}
          onClick={() => setActiveTab('geographic')}
        >
          Geographic
        </button>
        <button
          className={`tab-button ${activeTab === 'operations' ? 'active' : ''}`}
          onClick={() => setActiveTab('operations')}
        >
          Operations
        </button>
        <button
          className={`tab-button ${activeTab === 'costs' ? 'active' : ''}`}
          onClick={() => setActiveTab('costs')}
        >
          Costs & Finance
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'IMPS' && (
          <div className="section-container">
            <h2>IMPS Overview</h2>

            <div className="two-column-grid">
              {/* IMPS Summary Stats */}
              <div className="card">
                <h3>IMPS Status</h3>
                <div className="stat-grid">
                  <div className="stat-item">
                    <span className="label">Total</span>
                    <span className="value">{IMPS?.total_vehicles}</span>
                  </div>
                  <div className="stat-item">
                    <span className="label">Active</span>
                    <span className="value green">{IMPS?.active_vehicles}</span>
                  </div>
                  <div className="stat-item">
                    <span className="label">Inactive</span>
                    <span className="value red">{IMPS?.inactive_vehicles}</span>
                  </div>
                  <div className="stat-item">
                    <span className="label">Active %</span>
                    <span className="value">
                      {((IMPS?.active_vehicles / IMPS?.total_vehicles) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Assignment Summary */}
              <div className="card">
                <h3>Distribution</h3>
                <div className="stat-grid">
                  <div className="stat-item">
                    <span className="label">Departments</span>
                    <span className="value">{IMPS?.departments_with_vehicles}</span>
                  </div>
                  <div className="stat-item">
                    <span className="label">Brands</span>
                    <span className="value">{IMPS?.brands_in_IMPS}</span>
                  </div>
                  <div className="stat-item">
                    <span className="label">Models</span>
                    <span className="value">{IMPS?.models_in_IMPS}</span>
                  </div>
                  <div className="stat-item">
                    <span className="label">Stations</span>
                    <span className="value">{IMPS?.stations_operating}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Vehicles by Department */}
            <div className="card full-width">
              <h3>Top Departments by Vehicle Count</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Department</th>
                    <th>Total Vehicles</th>
                    <th>Active</th>
                    <th>Inactive</th>
                    <th>Active %</th>
                    <th>Photos</th>
                  </tr>
                </thead>
                <tbody>
                  {vehiclesByDepartment
                    ?.sort((a, b) => b.vehicle_count - a.vehicle_count)
                    .slice(0, 10)
                    .map((dept, i) => (
                      <tr key={i}>
                        <td>{dept.department_name}</td>
                        <td className="count">{dept.vehicle_count}</td>
                        <td className="count green">{dept.active}</td>
                        <td className="count red">{dept.inactive}</td>
                        <td>
                          {((dept.active / dept.vehicle_count) * 100).toFixed(1)}%
                        </td>
                        <td>{dept.photo_count}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'geographic' && (
          <div className="section-container">
            <h2>Geographic Distribution</h2>

            {/* Provinces */}
            <div className="card">
              <h3>Vehicles by Province</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Province</th>
                    <th>Departments</th>
                    <th>Vehicles</th>
                    <th>Active</th>
                    <th>Divisions</th>
                    <th>Districts</th>
                  </tr>
                </thead>
                <tbody>
                  {geographicDistribution
                    ?.sort((a, b) => b.vehicles - a.vehicles)
                    .map((prov, i) => (
                      <tr key={i}>
                        <td>{prov.province}</td>
                        <td>{prov.departments}</td>
                        <td className="count">{prov.vehicles}</td>
                        <td className="count green">{prov.active_vehicles}</td>
                        <td>{prov.divisions}</td>
                        <td>{prov.districts}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'operations' && (
          <div className="section-container">
            <h2>Operations & Maintenance</h2>

            <div className="two-column-grid">
              {/* Fuelling Stats */}
              <div className="card">
                <h3>Fuelling Operations</h3>
                <div className="stat-grid">
                  <div className="stat-item">
                    <span className="label">Fuelling Events</span>
                    <span className="value">{fuelling?.total_fuelling_records}</span>
                  </div>
                  <div className="stat-item">
                    <span className="label">Vehicles Fuelled</span>
                    <span className="value">{fuelling?.vehicles_fuelled}</span>
                  </div>
                  <div className="stat-item">
                    <span className="label">Total Spent</span>
                    <span className="value">
                      ₨{parseFloat(fuelling?.total_amount_spent || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="label">Avg per Fill</span>
                    <span className="value">
                      ₨{parseFloat(fuelling?.avg_fuel_cost || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Maintenance Stats */}
              <div className="card">
                <h3>Maintenance Records</h3>
                <div className="stat-grid">
                  <div className="stat-item">
                    <span className="label">Total Events</span>
                    <span className="value">{maintenance?.total_maintenance_records || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="label">Vehicles Maintained</span>
                    <span className="value">{maintenance?.vehicles_maintained || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="label">Maintenance Types</span>
                    <span className="value">{maintenance?.maintenance_types || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="label">Total Cost</span>
                    <span className="value">
                      ₨{parseFloat(maintenance?.total_maintenance_cost || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Documentation */}
            <div className="card">
              <h3>Vehicle Documentation</h3>
              <div className="stat-grid">
                <div className="stat-item">
                  <span className="label">Total Photos</span>
                  <span className="value">{documentation?.total_photos}</span>
                </div>
                <div className="stat-item">
                  <span className="label">Documented Vehicles</span>
                  <span className="value">{documentation?.vehicles_with_photos}</span>
                </div>
                <div className="stat-item">
                  <span className="label">Coverage</span>
                  <span className="value">{documentation?.photo_coverage_percentage}%</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'costs' && (
          <div className="section-container">
            <h2>Financial Analysis</h2>

            <div className="card full-width">
              <h3>Top Cost Departments</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Department</th>
                    <th>Vehicles</th>
                    <th>Fuel Cost</th>
                    <th>Maintenance Cost</th>
                    <th>Total Cost</th>
                    <th>Per Vehicle</th>
                  </tr>
                </thead>
                <tbody>
                  {costAnalysis
                    ?.slice(0, 10)
                    .map((dept, i) => (
                      <tr key={i}>
                        <td>{dept.department_name}</td>
                        <td>{dept.vehicles}</td>
                        <td>₨{parseFloat(dept.fuel_cost || 0).toFixed(2)}</td>
                        <td>₨{parseFloat(dept.maintenance_cost || 0).toFixed(2)}</td>
                        <td className="highlight">₨{parseFloat(dept.total_cost || 0).toFixed(2)}</td>
                        <td>₨{parseFloat(dept.cost_per_vehicle || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon, color }) => (
  <div className={`metric-card ${color}`}>
    <span className="metric-icon">{icon}</span>
    <div className="metric-content">
      <p className="metric-title">{title}</p>
      <p className="metric-value">{value || 0}</p>
    </div>
  </div>
);

export default IpmsAnalyticsDashboard;
